import { CSSProperties } from 'react';

export interface CommentMessageSegment {
  key: number;
  text: string;
  style?: CSSProperties;
}

const isQuillOps = (parsed: unknown): parsed is { ops: unknown[] } =>
  !!(
    typeof parsed === 'object' &&
    parsed !== null &&
    'ops' in parsed &&
    Array.isArray((parsed as { ops: unknown }).ops)
  );

/**
 * Parses a stored comment message. Plain-text messages are returned as-is; Quill
 * delta messages (`{ ops: [...] }`) are turned into render-ready segments carrying
 * their computed inline style (bold/italic/strike/underline, mentions render plain).
 * The trailing newline on the final op is trimmed to match the editor's output.
 */
export const parseCommentMessage = (message: string): string | CommentMessageSegment[] => {
  let ops: any[];
  try {
    const parsed = JSON.parse(message);
    if (!isQuillOps(parsed)) return message;
    ops = parsed.ops;
  } catch {
    return message;
  }

  const segments: CommentMessageSegment[] = [];
  ops.forEach((op: any, index: number) => {
    const isLast = index === ops.length - 1;
    const text =
      isLast && typeof op.insert === 'string' ? op.insert.replace(/\n+$/, '') : op.insert;

    if (!text) return;

    if (!op.attributes && !op.insert?.mention) {
      segments.push({ key: index, text, style: { whiteSpace: 'pre-line' } });
      return;
    }

    if (op.insert?.mention) {
      segments.push({ key: index, text: op.insert.mention.value });
      return;
    }

    segments.push({
      key: index,
      text,
      style: {
        textDecoration:
          op.attributes?.strike || op.attributes?.underline
            ? op.attributes?.strike
              ? 'line-through'
              : 'underline'
            : 'none',
        fontStyle: op.attributes?.italic ? 'italic' : 'normal',
        fontWeight: op.attributes?.bold ? 'bold' : 'normal',
        whiteSpace: 'pre-line',
      },
    });
  });

  return segments;
};
